import {
  Alert,
  AlertTitle,
  Box,
  CircularProgress,
  LinearProgress,
  Paper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { ToolEventMetadata } from "./TaskStatusTabs";
import { convertMarkdownToHtml } from "src/helpers/markdownUtils";

// Local type definition for event status
type EventStatus = {
  id: string;
  task_id?: string;
  title: string;
  message?: string;
  status: "pending" | "running" | "completed" | "failed";
  children: EventStatus[];
  parent_id?: string | null;
  timestamp?: string;
  progress?: number | null;
};

// Markdown renderer component
const MarkdownMessage: React.FC<{
  message: string;
  variant?: "body2" | "caption";
  color?: string;
  sx?: any;
}> = ({ message, variant = "body2", color = "text.secondary", sx }) => {
  const [htmlContent, setHtmlContent] = useState<string>("");

  useEffect(() => {
    const renderMarkdown = async () => {
      try {
        const html = await convertMarkdownToHtml(message);
        setHtmlContent(html);
      } catch (error) {
        console.error("Error converting markdown:", error);
        setHtmlContent(message);
      }
    };

    renderMarkdown();
  }, [message]);

  return (
    <Typography
      variant={variant}
      color={color}
      sx={sx}
      component="div"
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

// Recursive component for rendering nested events
interface RecursiveEventStepProps {
  event: EventStatus;
  depth: number;
  eventPath: string;
  expandedSteps: Set<string>;
  onToggleStep: (path: string) => void;
  getStepLabelProps: (step: EventStatus) => any;
  getStepIcon: (step: EventStatus) => React.ReactNode;
}

const RecursiveEventStep: React.FC<RecursiveEventStepProps> = ({
  event,
  depth,
  eventPath,
  expandedSteps,
  onToggleStep,
  getStepLabelProps,
  getStepIcon,
}) => {
  const isExpanded = expandedSteps.has(eventPath);
  const labelProps = getStepLabelProps(event);
  const hasChildren = event.children && event.children.length > 0;

  return (
    <Step
      key={event.id}
      active={event.status !== "completed" || isExpanded}
      completed={event.status === "completed"}
      expanded={isExpanded}
    >
      <StepLabel
        {...labelProps}
        icon={getStepIcon(event)}
        onClick={() =>
          (hasChildren || event.message) && onToggleStep(eventPath)
        }
        sx={{
          cursor: hasChildren || event.message ? "pointer" : "default",
          "& .MuiStepLabel-label": {
            color: event.status === "failed" ? "error.main" : "inherit",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            variant="body2"
            fontWeight={depth === 0 ? "medium" : "normal"}
            color={event.status === "failed" ? "error" : "inherit"}
          >
            {event.title}
            {hasChildren && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: "0.75rem",
                  color: "text.secondary",
                  opacity: 0.7,
                }}
              >
                ({event.children.length} subtask
                {event.children.length !== 1 ? "s" : ""})
              </Typography>
            )}
          </Typography>

          {/* Click indicator for completed tasks with content */}
          {event.status === "completed" && (hasChildren || event.message) && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "text.secondary",
                fontSize: "0.75rem",
                opacity: 0.7,
              }}
            >
              <span>•</span>
              <span>Click to {isExpanded ? "collapse" : "expand"}</span>
              <span
                style={{
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                  fontSize: "12px",
                }}
              >
                ▼
              </span>
            </Box>
          )}
        </Box>
      </StepLabel>

      {(isExpanded || (!hasChildren && event.message)) && (
        <StepContent>
          <Box
            ml={depth > 0 ? 1 : 2}
            my={1}
            sx={{
              borderLeft: depth > 1 ? "1px dotted rgba(0, 0, 0, 0.1)" : "none",
              pl: depth > 1 ? 2 : 0,
            }}
          >
            {event.message && event.status !== "failed" && (
              <>
                {event.title === "Result" ? (
                  (() => {
                    // Try to parse the message as JSON for artifact result
                    try {
                      const parsedMessage = JSON.parse(event.message);
                      if (
                        typeof parsedMessage === "object" &&
                        parsedMessage !== null &&
                        "artifact_id" in parsedMessage &&
                        "message" in parsedMessage
                      ) {
                        // Lazy import ArtifactDisplay to avoid import issues
                        const ArtifactDisplay = React.lazy(
                          () => import("./ArtifactDisplay"),
                        );
                        return (
                          <React.Suspense
                            fallback={<CircularProgress size={20} />}
                          >
                            <ArtifactDisplay
                              artifactId={parsedMessage.artifact_id}
                              artifactName={parsedMessage.artifact_name}
                              message={parsedMessage.message}
                            />
                          </React.Suspense>
                        );
                      }
                    } catch (error) {
                      // If parsing fails, fall back to markdown rendering
                      console.warn(
                        "Failed to parse result message as JSON:",
                        error,
                      );
                    }
                    return (
                      <MarkdownMessage
                        message={event.message}
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: hasChildren ? 2 : 1,
                        }}
                      />
                    );
                  })()
                ) : (
                  <MarkdownMessage
                    message={event.message}
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: hasChildren ? 2 : 1,
                    }}
                  />
                )}
              </>
            )}

            {hasChildren && (
              <Box>
                <Stepper
                  orientation="vertical"
                  nonLinear
                  sx={{
                    "& .MuiStepConnector-root": {
                      marginLeft: depth > 2 ? "10px" : "12px",
                    },
                  }}
                >
                  {event.children.map((childEvent, childIndex) => (
                    <RecursiveEventStep
                      key={childEvent.id}
                      event={childEvent}
                      depth={depth + 1}
                      eventPath={`${eventPath}-${childIndex}`}
                      expandedSteps={expandedSteps}
                      onToggleStep={onToggleStep}
                      getStepLabelProps={getStepLabelProps}
                      getStepIcon={getStepIcon}
                    />
                  ))}
                </Stepper>
              </Box>
            )}
          </Box>
        </StepContent>
      )}
    </Step>
  );
};

interface TaskTrackerProps {
  isLoading: boolean;
  taskResult: ToolEventMetadata | undefined;
}

const TaskTracker: React.FC<TaskTrackerProps> = ({ isLoading, taskResult }) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  const previousTaskResultRef = useRef<ToolEventMetadata | undefined>();

  // Smart auto-expand: only expand new workflow elements while preserving user choices
  useEffect(() => {
    if (taskResult && taskResult.events) {
      // Helper function to collect all expandable paths
      const collectExpandablePaths = (
        events: EventStatus[],
        pathPrefix = "",
      ): Set<string> => {
        const paths = new Set<string>();
        events.forEach((event, index) => {
          const currentPath = pathPrefix
            ? `${pathPrefix}-${index}`
            : `${index}`;
          if (event.children && event.children.length > 0) {
            paths.add(currentPath);
            const childPaths = collectExpandablePaths(
              event.children,
              currentPath,
            );
            childPaths.forEach((path) => paths.add(path));
          }
        });
        return paths;
      };

      const currentExpandablePaths = collectExpandablePaths(
        taskResult.events as EventStatus[],
      );

      if (!isInitialized) {
        // First time loading - auto-expand all
        setExpandedSteps(currentExpandablePaths);
        setIsInitialized(true);
      } else {
        // Subsequent updates - only expand NEW paths, preserve user choices
        const previousTaskResult = previousTaskResultRef.current;
        if (previousTaskResult && previousTaskResult.events) {
          const previousExpandablePaths = collectExpandablePaths(
            previousTaskResult.events as EventStatus[],
          );

          setExpandedSteps((prev) => {
            const newExpandedSteps = new Set(prev);

            // Only add NEW paths that weren't in the previous state
            currentExpandablePaths.forEach((path) => {
              if (!previousExpandablePaths.has(path)) {
                newExpandedSteps.add(path);
              }
            });

            // Remove paths that no longer exist
            Array.from(prev).forEach((path) => {
              if (!currentExpandablePaths.has(path)) {
                newExpandedSteps.delete(path);
              }
            });

            return newExpandedSteps;
          });
        }
      }

      // Store current taskResult for next comparison
      previousTaskResultRef.current = taskResult;
    }
  }, [taskResult, isInitialized]);

  const toggleStep = (path: string) => {
    setExpandedSteps((prevExpanded) => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(path)) {
        newExpanded.delete(path);
      } else {
        newExpanded.add(path);
      }
      return newExpanded;
    });
  };

  const getStepLabelProps = useCallback((step: EventStatus) => {
    const labelProps: {
      optional?: React.ReactNode;
      error?: boolean;
    } = {};

    if (step.status === "failed") {
      labelProps.error = true;
      labelProps.optional = (
        <Typography variant="caption" color="error">
          {step.message || "Step failed"}
        </Typography>
      );
    }

    return labelProps;
  }, []);

  const getStepIcon = useCallback((step: EventStatus) => {
    if (step.status === "completed") {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "14px",
          }}
        >
          ✓
        </Box>
      );
    }
    if (step.status === "failed") {
      return (
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            backgroundColor: "error.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontSize: "14px",
          }}
        >
          ✗
        </Box>
      );
    }
    return <CircularProgress size={20} />;
  }, []);

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography>Loading tool tracking data...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!taskResult) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No task tracking data available.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined">
      <Box sx={{ width: "100%" }}>
        {(taskResult.status.toLowerCase() === "active" ||
          taskResult.status.toLowerCase() === "running") && (
          <LinearProgress color="primary" />
        )}
      </Box>
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {taskResult.title || taskResult.event_type || "Task Progress"}
        </Typography>

        <Box mb={3}>
          <Typography variant="body1" fontWeight="medium" gutterBottom>
            {`Status: ${
              taskResult.status.charAt(0).toUpperCase() +
              taskResult.status.slice(1)
            }`}
          </Typography>

          {taskResult.status.toLowerCase() === "completed" && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Task Completed Successfully</AlertTitle>
              Task has completed successfully.
            </Alert>
          )}

          {taskResult.status.toLowerCase() === "failed" && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Task Failed</AlertTitle>
              An error occurred while executing the task.
            </Alert>
          )}
        </Box>

        {taskResult.events && (
          <Stepper orientation="vertical">
            {(taskResult.events as EventStatus[]).map((event, index) => (
              <RecursiveEventStep
                key={event.id}
                event={event}
                depth={0}
                eventPath={`${index}`}
                expandedSteps={expandedSteps}
                onToggleStep={toggleStep}
                getStepLabelProps={getStepLabelProps}
                getStepIcon={getStepIcon}
              />
            ))}
          </Stepper>
        )}
      </Box>
    </Paper>
  );
};

export default TaskTracker;
